import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Table } from 'antd-v5';
import PropTypes from 'prop-types';
import { useAudiencePreview } from '../../../hooks/query/useAudiences';
import formatPhoneNumber from '../../../utils/formatPhoneNumber';

const PAGE_SIZE = 20;

const columns = [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    sorter: true,
  },
  {
    title: 'Mobile Number',
    dataIndex: 'mobile',
    key: 'mobile',
    render: val => (val ? formatPhoneNumber(val, 'b') : '—'),
  },
  {
    title: 'Email Address',
    dataIndex: 'email',
    key: 'email',
    render: val => val || '—',
  },
  {
    title: 'Marketing',
    dataIndex: 'marketing',
    key: 'marketing',
    render: val => (val ? 'Yes' : 'No'),
  },
];

const AudiencePreviewModal = ({ open, onClose, configuration, total }) => {
  const [clients, setClients] = useState([]);
  const [currentTotal, setCurrentTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState(undefined);

  const previewMutation = useAudiencePreview();

  const fetchClients = useCallback(
    (pageNum, sortVal) => {
      previewMutation.mutate(
        {
          configuration,
          limit: PAGE_SIZE,
          offset: (pageNum - 1) * PAGE_SIZE,
          sort: sortVal,
        },
        {
          onSuccess: data => {
            setClients(data?.clients || []);
            setCurrentTotal(data?.total ?? 0);
          },
        },
      );
    },
    [configuration], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (open) {
      setPage(1);
      setSort(undefined);
      fetchClients(1, undefined);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTableChange = (pagination, _filters, sorter) => {
    const newPage = pagination.current;
    let newSort;
    if (sorter.order) {
      newSort = sorter.order === 'descend' ? `-${sorter.field}` : sorter.field;
    }
    setPage(newPage);
    setSort(newSort);
    fetchClients(newPage, newSort);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Audience Preview (${(total ?? currentTotal).toLocaleString()} Clients)`}
      footer={null}
      width={860}
      centered
    >
      <Table
        columns={columns}
        dataSource={clients}
        rowKey="id"
        loading={previewMutation.isLoading}
        onChange={handleTableChange}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: currentTotal,
          showSizeChanger: false,
          showTotal: t => `${t} clients`,
        }}
        size="small"
      />
    </Modal>
  );
};

AudiencePreviewModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  configuration: PropTypes.object.isRequired,
  total: PropTypes.number,
};

AudiencePreviewModal.defaultProps = {
  total: undefined,
};

export default AudiencePreviewModal;
